const positive = (value) => Number.isFinite(Number(value)) && Number(value) > 0;

export const toMetricUnitKey = (value) => {
  const unit = String(value || '').trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, ' ');
  if (unit.includes('kg') || unit.includes('kilo')) return 'kg';
  return ['caja', 'cajon', 'saco', 'malla', 'unidad', 'balde', 'gamela', 'plancha', 'bandeja', 'bin', 'granel']
    .find((key) => unit.includes(key)) || unit;
};

export const buildWholesalePriceMap = (products = []) => {
  const result = {};
  products.forEach((product) => (product.Varieties || []).forEach((variety) => {
    result[variety.id] = (variety.VarietyPrices || []).filter((format) =>
      String(format.clientType || format.productFormatCode || '').toLowerCase() === 'mayorista'
      && !format.retiredAt && format.isActive !== false && format.productFormatIsActive !== false
      && positive(format.price)
    ).map((format) => ({ ...format, inventoryBaseUnit: variety.inventoryBaseUnit,
      categoryIsResaleOnly: product.Category?.isResaleOnly }));
  }));
  return result;
};

// Purchase counts and selling-format counts are different quantities. Only
// compare prices after converting through recorded weights or an explicit ratio.
export const resolveStockWholesaleEstimate = ({ stock, entries = [], equivalenceMap,
  quantity = Number(stock?.quantity), totalWeightKg }) => {
  if (!positive(quantity)) return { considered: false, reason: 'Cantidad inválida o no informada.' };
  const purchaseMetricUnit = stock?.purchaseMode === 'granel' ? 'kg'
    : stock?.purchaseSnapshot?.metricUnit || stock?.metricUnit || '';
  const sourceKey = toMetricUnitKey(purchaseMetricUnit);
  const sourceWeight = stock?.purchaseSnapshot?.weightKg ?? stock?.weightKg ?? stock?.weight;
  const sourceBaseUnit = stock?.purchaseSnapshot?.inventoryBaseUnit || stock?.Variety?.inventoryBaseUnit;
  const canUseWeight = (!sourceBaseUnit || sourceBaseUnit === 'kg') && positive(totalWeightKg)
    && (positive(sourceWeight) || ['kg', 'granel'].includes(sourceKey));
  const selectedId = stock?.sellingVarietyPriceId || stock?.purchaseVarietyPriceId;
  let selectedEntries = selectedId ? entries.filter((entry) => Number(entry.id) === Number(selectedId)) : entries;
  const preview = stock?.wholesalePricePreview;
  if (selectedId && !selectedEntries.some((entry) => positive(entry.price))
    && Number(preview?.varietyPriceId) === Number(selectedId) && positive(preview?.sellingPrice)) {
    selectedEntries = [{ id: selectedId, metricUnit: preview.metricUnit, unitWeightKg: preview.unitWeightKg,
      price: preview.sellingPrice, inventoryBaseUnit: sourceBaseUnit, priceSource: 'draft' }];
  }
  const candidates = selectedEntries.flatMap((entry) => {
    if (!positive(entry.price) || entry.retiredAt) return [];
    const targetKey = toMetricUnitKey(entry.metricUnit);
    if (!targetKey) return [];
    const targetWeightKg = positive(entry.unitWeightKg) ? Number(entry.unitWeightKg) : targetKey === 'kg' ? 1 : null;
    const targetUsesWeight = (!entry.inventoryBaseUnit || entry.inventoryBaseUnit === 'kg') && !entry.categoryIsResaleOnly;
    let sellingQuantity;
    let mode;
    let equivalence = null;
    if (canUseWeight && targetUsesWeight && positive(targetWeightKg)) {
      sellingQuantity = Number(totalWeightKg) / targetWeightKg;
      mode = 'weight';
    } else {
      equivalence = (equivalenceMap?.get(sourceKey) || []).find((ratio) =>
        ratio.targetUnitKey === targetKey && positive(ratio.multiplier));
      if (equivalence) {
        sellingQuantity = quantity * Number(equivalence.multiplier);
        mode = 'equivalence';
      } else if (sourceKey && sourceKey === targetKey && !positive(sourceWeight) && !positive(targetWeightKg)) {
        sellingQuantity = quantity;
        mode = 'exact';
      } else return [];
    }
    const unitPrice = Number(entry.price);
    return [{ considered: true, estimate: sellingQuantity * unitPrice, unitPrice, rawUnitPrice: unitPrice,
      metricUnit: entry.metricUnit, wholesaleMetricUnit: entry.metricUnit,
      varietyPriceId: entry.id, quantity: sellingQuantity, purchaseQuantity: quantity, purchaseMetricUnit,
      totalWeightKg: mode === 'weight' ? Number(totalWeightKg) : null,
      sellingWeightKg: targetWeightKg, mode, usedEquivalence: mode === 'equivalence', equivalence,
      priceSource: entry.priceSource || 'catalog' }];
  });
  // Compare the total for the same purchased stock, not container sticker prices.
  candidates.sort((a, b) => a.estimate - b.estimate || Number(a.varietyPriceId || 0) - Number(b.varietyPriceId || 0));
  if (candidates.length) return candidates[0];
  // Keep a known sales format visible even when its conversion is incomplete.
  // Multiple formats without a valid conversion remain unresolved.
  const knownFormat = selectedEntries.length === 1 ? selectedEntries[0] : null;
  return { considered: false, wholesaleMetricUnit: knownFormat?.metricUnit,
    sellingWeightKg: positive(knownFormat?.unitWeightKg) ? Number(knownFormat.unitWeightKg)
      : toMetricUnitKey(knownFormat?.metricUnit) === 'kg' ? 1 : null,
    reason: selectedId && !selectedEntries.length
    ? 'El formato de venta seleccionado no tiene un precio mayorista disponible.'
    : entries.length ? 'Falta el peso del formato de venta o una equivalencia válida entre compra y venta.'
      : 'Sin precio mayorista disponible para esta variedad.' };
};
